import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useProjectParams} from '../../../../pages/ProjectPage/useProjectParams';
import {SeparatedChunk} from './shared';
import {EventDescriptionSection} from './WeaveMetricAlert/components/EventDescriptionSection';
import {ExampleMetric} from './WeaveMetricAlert/components/ExampleMetric';
import {MetricConfiguration} from './WeaveMetricAlert/components/MetricConfiguration';
import {MetricPathSelector} from './WeaveMetricAlert/components/MetricPathSelector';
import {SourceSelector} from './WeaveMetricAlert/components/SourceSelector';
import {SpanMetricSelector} from './WeaveMetricAlert/components/SpanMetricSelector';
import {WeaveMetricChart} from './WeaveMetricAlert/components/WeaveMetricChart';
import {WindowConfig} from './WeaveMetricAlert/components/WindowConfig';
import {spanAggregationOptions} from './WeaveMetricAlert/constants';
import {useGetMetricFromSource} from './WeaveMetricAlert/hooks';
import {
  useWeaveMetricThresholdStore,
  WeaveMetricThresholdActions,
} from './WeaveMetricAlert/store';
import {type MetricType} from './WeaveMetricAlert/types';
import {
  getWeaveMetricThresholdString,
  inferType,
  resolveMetricType,
  spanMetricType,
} from './WeaveMetricAlert/utils';
import {
  isValidMetricPath,
  isValidThreshold,
  isValidWindowDuration,
  isValidWindowSize,
} from './WeaveMetricAlert/validation';

export {getWeaveAlertTrackProps} from './WeaveMetricAlert/analytics';
export {
  getWeaveMetricThresholdConfig,
  useWeaveMetricThresholdStore,
  WeaveMetricThresholdActions,
} from './WeaveMetricAlert/store';
export type {WeaveMetricThresholdConfig} from './WeaveMetricAlert/types';
export {
  convertSecondsToTimeUnit,
  convertTimeToSeconds,
  getWeaveMetricThresholdString,
} from './WeaveMetricAlert/utils';
export {isValidWeaveMetricThresholdConfig} from './WeaveMetricAlert/validation';

type WeaveMetricThresholdSectionInternalProps = {
  entityName?: string;
  projectName?: string;
};

const WeaveMetricThresholdSectionInternal = ({
  entityName,
  projectName,
}: WeaveMetricThresholdSectionInternalProps) => {
  const metricFilter = useWeaveMetricThresholdStore(
    useShallow(state => state.metricFilter)
  );
  const metricData = useGetMetricFromSource(
    entityName ?? '',
    projectName ?? '',
    metricFilter.sourceType ?? 'OPERATION',
    metricFilter.opStr ?? '',
    metricFilter.monitorRef ?? ''
  );

  const isSpans = metricFilter.sourceType === 'SPANS';
  const metricType: MetricType = resolveMetricType(
    metricFilter.sourceType,
    metricFilter.metricPath,
    metricData.call
  );

  const handleMetricPathChange = (value: string) => {
    if (isSpans) {
      if (spanMetricType(value) === 'span_status') {
        WeaveMetricThresholdActions.setAggregationFunction('percentage');
        WeaveMetricThresholdActions.setThreshold('10');
        WeaveMetricThresholdActions.setComparisonOperator('GREATER_THAN');
      } else {
        WeaveMetricThresholdActions.setAggregationFunction('mean');
        WeaveMetricThresholdActions.setComparisonOperator('GREATER_THAN');
        // Leaving error rate: drop the stale percentage so it isn't carried
        // over as a raw numeric threshold.
        if (metricType === 'span_status') {
          WeaveMetricThresholdActions.setThreshold('');
        }
      }
      WeaveMetricThresholdActions.setMetricPath(value);
      return;
    }

    const newType = metricData.call
      ? inferType(metricData.call, value)
      : 'float';

    if (newType === 'status') {
      // Status metrics use percentage/count aggregation with a separate status match value
      WeaveMetricThresholdActions.setStatusMatchValue('2'); // default to error
      WeaveMetricThresholdActions.setAggregationFunction('percentage');
      WeaveMetricThresholdActions.setThreshold('10'); // default 10%
      WeaveMetricThresholdActions.setComparisonOperator('GREATER_THAN');
    } else if (newType === 'bool') {
      WeaveMetricThresholdActions.setAggregationFunction('mean');
      WeaveMetricThresholdActions.setThreshold('1');
      WeaveMetricThresholdActions.setComparisonOperator('EQUAL');
    } else if (newType === 'float') {
      WeaveMetricThresholdActions.setAggregationFunction('mean');
      WeaveMetricThresholdActions.setComparisonOperator('GREATER_THAN');
    }

    WeaveMetricThresholdActions.setMetricPath(value);
  };

  const showMetricPathError =
    metricFilter.metricPath !== undefined &&
    !isValidMetricPath(metricFilter.metricPath);
  const showThresholdError =
    metricFilter.threshold !== undefined &&
    !isValidThreshold(
      metricFilter.threshold,
      metricType,
      metricFilter.aggregationFunction
    );
  // When editing an existing status metric trigger, statusMatchValue may not yet be set
  // in the store even though the UI shows a default. Ensure the store is synced.
  React.useEffect(() => {
    if (
      metricType === 'status' &&
      metricFilter.statusMatchValue === undefined
    ) {
      WeaveMetricThresholdActions.setStatusMatchValue('2');
    }
  }, [metricType, metricFilter.statusMatchValue]);

  const showStatusMatchError =
    metricType === 'status' && metricFilter.statusMatchValue === undefined;
  const showWindowSizeError =
    metricFilter.windowSize !== undefined &&
    !isValidWindowSize(metricFilter.windowSize);
  const showTimeWindowError =
    metricFilter.windowDuration !== undefined &&
    !isValidWindowDuration(metricFilter.windowDuration);

  const eventDescription = `Alert when ${getWeaveMetricThresholdString(
    metricFilter
  )}`;

  return (
    <Tailwind>
      <div className="mt-16 font-semibold">Metric setup</div>
      <div className="mt-8 flex flex-col rounded-lg border border-wb-border p-16">
        <div className="mb-16">
          <EventDescriptionSection eventDescription={eventDescription} />
        </div>
        <div className="font-semibold">Source</div>
        <div className="mt-4">
          <SourceSelector
            entityName={entityName || ''}
            projectName={projectName || ''}
            metricFilter={metricFilter}
          />
        </div>
        <SeparatedChunk>
          <div className="font-semibold">Metric</div>
          <div className="mt-4">
            {isSpans ? (
              <SpanMetricSelector
                selectedPath={metricFilter.metricPath || ''}
                onPathChange={handleMetricPathChange}
                hasError={showMetricPathError}
              />
            ) : (
              <>
                <MetricPathSelector
                  entityName={entityName || ''}
                  projectName={projectName || ''}
                  sourceType={metricFilter.sourceType || 'OPERATION'}
                  opStr={metricFilter.opStr || ''}
                  monitorRef={metricFilter.monitorRef || ''}
                  selectedPath={metricFilter.metricPath || ''}
                  onPathChange={handleMetricPathChange}
                  hasError={showMetricPathError}
                />
                {metricFilter.metricPath && metricData.call && (
                  <ExampleMetric
                    metricPath={metricFilter.metricPath}
                    call={metricData.call}
                    metricType={metricType}
                  />
                )}
              </>
            )}
          </div>
        </SeparatedChunk>
        <MetricConfiguration
          metricType={metricType}
          threshold={metricFilter.threshold || ''}
          operator={metricFilter.comparisonOperator}
          onThresholdChange={WeaveMetricThresholdActions.setThreshold}
          onOperatorChange={WeaveMetricThresholdActions.setComparisonOperator}
          hasError={showThresholdError}
          statusMatchValue={metricFilter.statusMatchValue}
          aggregationFunction={metricFilter.aggregationFunction}
          onStatusMatchValueChange={
            WeaveMetricThresholdActions.setStatusMatchValue
          }
          onAggregationFunctionChange={
            WeaveMetricThresholdActions.setAggregationFunction
          }
          hasStatusError={showStatusMatchError}
        />
        <WindowConfig
          windowSize={metricFilter.windowSize}
          windowDuration={metricFilter.windowDuration}
          aggregationFunction={metricFilter.aggregationFunction}
          onWindowSizeChange={WeaveMetricThresholdActions.setWindowSize}
          onWindowDurationChange={WeaveMetricThresholdActions.setWindowDuration}
          onAggregationFunctionChange={
            WeaveMetricThresholdActions.setAggregationFunction
          }
          hasCountError={showWindowSizeError}
          hasTimeError={showTimeWindowError}
          metricType={metricType}
          durationOnly={isSpans}
          aggregationOptions={isSpans ? spanAggregationOptions : undefined}
        />
        {entityName &&
          projectName &&
          metricFilter.metricPath &&
          (isSpans || metricFilter.opStr) && (
            <SeparatedChunk>
              <div className="font-semibold">Alert preview</div>
              <div className="mt-4">
                <WeaveMetricChart
                  entityName={entityName}
                  projectName={projectName}
                  config={{metricFilter, allowAutoFocus: false}}
                />
              </div>
            </SeparatedChunk>
          )}
      </div>
    </Tailwind>
  );
};

export const WeaveMetricThresholdSection = () => {
  const {entityName, projectName} = useProjectParams();
  return (
    <WeaveMetricThresholdSectionInternal
      entityName={entityName}
      projectName={projectName}
    />
  );
};
